import {booleanAttribute, ChangeDetectorRef, Component, ElementRef, forwardRef, Input, NgZone, OnDestroy, ViewChild} from '@angular/core';
import {SubscribedComponent} from "../../subscribed.component";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {NbComponentOrCustomStatus, NbComponentSize, NbComponentShape, NbFormControlState} from "@nebular/theme";
import {parsePlace, PlaceAddress, placesAvailable, suggestionsOpen} from './place-address';
import {GoogleMapsService} from "../../shared/google-maps.service";

@Component({
    selector: 'bst-places-editor',
    templateUrl: './places-editor.component.html',
    styleUrls: ['./places-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PlacesEditorComponent),
            multi: true
        }
    ],
    standalone: false
})
export class PlacesEditorComponent extends SubscribedComponent implements
  ControlValueAccessor, NbFormControlState, OnDestroy{

  @ViewChild("field", {static: true}) field: ElementRef;

  @Input() placeholder: string;
  @Input() focused: boolean = false;
  @Input({transform: booleanAttribute}) fullWidth = false;
  @Input() size: NbComponentSize = 'medium';
  @Input() shape: NbComponentShape = 'rectangle';
  @Input() maxlength: number = 1000;
  @Input() status: NbComponentOrCustomStatus = 'basic';
  @Input() style = null;
  /** 'string' emits the text as typed or picked; 'structured' emits a PlaceAddress on a pick only, null otherwise. */
  @Input() mode: 'string' | 'structured' = 'string';

  onChange = (value: string | PlaceAddress) => {};
  onTouched = () => {};
  public address: string;
  public disabled: boolean = false;
  /** Translation key shown under the field; structured mode only. */
  public message: string = null;
  // Structured mode: the current value came from a pick (or the model), not from typing.
  private picked = false;
  private handle: google.maps.places.Autocomplete;

  constructor(private detector: ChangeDetectorRef, private zone: NgZone, private maps: GoogleMapsService) {
    super();
  }

  // Google's key-rejection callback fires async (GoogleMapsService dispatches this), so a
  // field already rendered as available needs to flip to unavailable without a reload.
  private onAuthFailure = () => this.zone.run(() => {
    if (this.mode === 'structured') { this.message = 'places.unavailable'; }
    this.detector.markForCheck();
  });

  ngOnInit(): void {
    super.ngOnInit();
    // The Maps script is only fetched once a field like this is shown (#1755).
    const loaded = this.maps.load();
    this.attach();
    if (!this.maps.settled) {
      loaded.then(() => this.zone.run(() => { this.attach(); this.detector.markForCheck(); }));
    }
    window.addEventListener('gm-auth-failure', this.onAuthFailure);
  }

  /** The Maps script loads async, so a field rendered before it retries on focus. */
  attach() {
    if (this.handle || !this.maps.settled) { return; }
    if (!placesAvailable()) {
      if (this.mode === 'structured') { this.message = 'places.unavailable'; }
      return;
    }
    this.message = null;
    this.handle = new google.maps.places.Autocomplete(this.field.nativeElement, {fields: ['address_components', 'formatted_address', 'name']});
    google.maps.event.addListener(this.handle, 'place_changed', () => this.zone.run(() => this.placed()));
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if(this.handle){
      google.maps.event.clearListeners(this.handle, 'place_changed');
    }
    window.removeEventListener('gm-auth-failure', this.onAuthFailure);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: string | PlaceAddress): void {
    this.address = (typeof value === 'string' ? value : value?.formatted) || null;
    this.picked = !!value;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  typed() {
    this.address = this.field.nativeElement.value || null;
    if (this.mode === 'string') { this.onChange(this.address); return; }
    if (this.message !== 'places.unavailable') { this.message = null; }
    if (this.picked) { this.picked = false; this.onChange(null); }
  }

  // Google moves through and picks from its list on keydown; Enter must not also submit the form or dialog around us,
  // and Escape, which closes the list, must not also close a dialog around us.
  keydown($event: KeyboardEvent) {
    if (!suggestionsOpen()) { return; }
    if ($event.key === 'Enter') {
      $event.preventDefault();
      $event.stopPropagation();
    } else if ($event.key === 'Escape') {
      $event.stopPropagation();
    }
  }

  blurred() {
    this.onTouched();
    if (this.mode === 'structured' && this.handle && this.address && !this.picked) { this.message = 'places.pick'; }
  }

  private placed() {
    this.address = this.field.nativeElement.value || null;
    if (this.mode === 'string') {
      this.onChange(this.address);
    } else {
      const place = parsePlace(this.handle.getPlace());
      this.picked = !!place;
      this.message = place ? null : 'places.no_result';
      this.onChange(place);
    }
    this.onTouched();
    this.detector.detectChanges();
  }
}
